import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  Alert,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import StockService from '../../services/stock.service';
import { NumberTextField } from '../../components/common';
import ProductService from '../../services/product.service';
import WarehouseService from '../../services/warehouse.service';

const StockForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [products, setProducts] = useState<any[]>([]);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [formData, setFormData] = useState<any>({
    product: '',
    warehouse: '',
    quantity: 0,
    minQuantity: 0,
  });

  useEffect(() => {
    fetchProducts();
    fetchWarehouses();
    if (isEdit && id) {
      fetchStock(parseInt(id));
    }
  }, [id, isEdit]);

  const fetchProducts = async () => {
    try {
      const response = await ProductService.getAll({ populate: '*' });
      setProducts(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const response = await WarehouseService.getAll({ populate: '*' });
      setWarehouses(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching warehouses:', error);
    }
  };

  const fetchStock = async (stockId: number) => {
    try {
      const response = await StockService.getOne(stockId);
      const stock = response.data?.data?.attributes;
      if (stock) {
        setFormData({
          ...stock,
          product: stock.product?.data?.id,
          warehouse: stock.warehouse?.data?.id,
        });
      }
    } catch (error) {
      console.error('Error fetching stock:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        lastUpdated: new Date().toISOString(),
      };

      if (isEdit && id) {
        await StockService.update(parseInt(id), submitData);
      } else {
        await StockService.create(submitData);
      }
      navigate('/stock');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to save stock');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit Stock Entry' : 'Create Stock Entry'}
      </Typography>

      <Paper sx={{ p: 3, mt: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <TextField
                fullWidth
                select
                required
                label="Product"
                name="product"
                value={formData.product || ''}
                onChange={(e) =>
                  setFormData((prev: any) => ({
                    ...prev,
                    product: e.target.value ? parseInt(e.target.value) : undefined,
                  }))
                }
              >
                <MenuItem value="">Select Product</MenuItem>
                {products.map((prod) => (
                  <MenuItem key={prod.id} value={prod.id}>
                    {prod.attributes?.name} ({prod.attributes?.sku})
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                select
                required
                label="Warehouse"
                name="warehouse"
                value={formData.warehouse || ''}
                onChange={(e) =>
                  setFormData((prev: any) => ({
                    ...prev,
                    warehouse: e.target.value ? parseInt(e.target.value) : undefined,
                  }))
                }
              >
                <MenuItem value="">Select Warehouse</MenuItem>
                {warehouses.map((wh) => (
                  <MenuItem key={wh.id} value={wh.id}>
                    {wh.attributes?.name} ({wh.attributes?.code})
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <NumberTextField
                fullWidth
                required
                label="Quantity"
                name="quantity"
                value={formData.quantity || 0}
                onChange={handleChange}
                inputProps={{ min: '0' }}
                allowDecimal={false}
              />
              <TextField
                fullWidth
                label="Location"
                name="location"
                value={formData.location || ''}
                onChange={handleChange}
                placeholder="e.g., Aisle 3, Shelf B"
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
              <NumberTextField
                fullWidth
                label="Minimum Quantity"
                name="minQuantity"
                value={formData.minQuantity || 0}
                onChange={handleChange}
                inputProps={{ min: '0' }}
                allowDecimal={false}
              />
              <NumberTextField
                fullWidth
                label="Maximum Quantity"
                name="maxQuantity"
                value={formData.maxQuantity || ''}
                onChange={handleChange}
                inputProps={{ min: '0' }}
                allowDecimal={false}
              />
            </Stack>

            <NumberTextField
              fullWidth
              label="Reorder Point"
              name="reorderPoint"
              value={formData.reorderPoint || ''}
              onChange={handleChange}
              inputProps={{ min: '0' }}
              helperText="Alert when stock reaches this level"
              allowDecimal={false}
            />

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/stock')}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default StockForm;

